import { memo, useCallback, useMemo } from 'react';
import { Progress } from 'radix-ui';
import { useGameDispatch } from '../../store/global-context';
import { useGeneratorsStore } from '../../store/zustand/useGeneratorsStore';
import { useCurrencyStore } from '../../store/zustand/useCurrencyStore';
import styles from './styles.module.scss';
import { UpgradeActions, UpgradeType } from '../../types/game-types';
import { SmallButton } from '../components/SmallButton';
import { Icon } from '../Icon';
import { Debug, humanNumber, HotkeyHint } from '@game-libs/exc-libs';
import { useHotkeys } from 'react-hotkeys-hook';
import { HelperTooltip } from '../components/HelperTooltip';
import { ManaMeterTooltipContent } from '../components/TooltipContents/ManaMeterTooltipContent';
import tooltipStyles from '../components/TooltipContents/styles.module.scss';
import { upgradesConfig } from '../../upgrades-config';
import { costToBuyNItems } from '../../store/helpers/calculate-cost';
import { useUpgradesStore } from '../../store/zustand/useUpgradesStore';

export const ManaMeter = memo(function ManaMeter() {
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const generators = useGeneratorsStore((store) => store.generators);
  const mana = useCurrencyStore((state) => state.mana);
  const gold = useCurrencyStore((state) => state.gold);
  const dispatch = useGameDispatch();

  // ManaSurge upgrade - must be called before any early returns (Rules of Hooks)
  const manaSurgeConfig = useMemo(
    () => upgradesConfig.wizard!.find((upgrade) => upgrade.id === UpgradeType.ManaSurge),
    [],
  );

  const maxMana = upgrades.wizard.manaPool.multiplier;

  // Calculate the manaBoost threshold from upgrades
  const manaBoost = upgrades.wizard.manaBoost?.multiplier ?? 0;

  // Check if mana is above the manaBoost threshold
  const isManaBoosted = mana > manaBoost;
  const manaSurge = upgrades.wizard.manaSurge;
  const manaSurgeCost = costToBuyNItems(manaSurge.cost.gold!, manaSurge.costGrowthRate, manaSurge.purchased, 1);
  const canAffordManaSurge = gold >= manaSurgeCost;

  const handleManaSurge = useCallback(() => {
    if (!canAffordManaSurge) return;
    dispatch({ type: UpgradeActions.BUY_UPGRADE, payload: { id: UpgradeType.ManaSurge, entityType: 'wizard' } });

    const defaultUnlockAtValue = 3;
    const unlockAmountPurchased =
      manaSurge.purchased + 1 === (manaSurgeConfig!.subTreeConfig?.unlockAt ?? defaultUnlockAtValue);

    if (manaSurge.purchased === 0) {
      dispatch({
        type: UpgradeActions.SHOW_UPGRADE,
        payload: { children: manaSurgeConfig!.children, entityType: 'wizard' },
      });
    }
    if (unlockAmountPurchased) {
      dispatch({
        type: UpgradeActions.UNLOCK_UPGRADE,
        payload: { children: manaSurgeConfig!.children, entityType: 'wizard' },
      });
    }
  }, [
    canAffordManaSurge,
    dispatch,
    manaSurge.purchased,
    manaSurgeConfig,
  ]);

  useHotkeys(
    'r',
    () => {
      if (canAffordManaSurge) handleManaSurge();
    },
    [canAffordManaSurge, handleManaSurge],
  );

  if (generators.wizard.owned === 0) {
    return null;
  }

  return (
    <div className={`${styles.manaMeter} ${mana <= 0 ? styles.isManaEmpty : ''} ${isManaBoosted ? styles.isManaBoosted : ''}`}>
      <div className={styles.manaButton}>
        {mana <= 0 && <div className={styles.buyMeArrow}>
          <Icon iconId={'buy_me_arrow'} />
        </div>}
        <HelperTooltip
          content={
            <span className={styles.priceTooltip}>
              <Icon iconId="item_cost_gold" />
              {humanNumber(manaSurgeCost, 1)}
            </span>
          }
          contentClassName={tooltipStyles.tooltipContent}
          disableHoverableContent
        >
          <SmallButton disabled={!canAffordManaSurge} onClick={handleManaSurge} shadow>
            <Icon iconId={'mana'} large hasBackground={false} />
            <HotkeyHint hint="r" />
          </SmallButton>
        </HelperTooltip>
      </div>

      <HelperTooltip content={<ManaMeterTooltipContent />} contentClassName={tooltipStyles.tooltipContent}>
        <Progress.Root
          className={`${styles.manaBar} ${isManaBoosted ? styles.boosted : ''}`}
          id="mana"
          value={mana}
          max={maxMana}
        >
          <Progress.Indicator
            className={styles.manaBarValue}
            style={{ width: `${(100 * mana) / maxMana}%` }}
          />
        </Progress.Root>
      </HelperTooltip>

      <Debug alwaysShow absolute><span className={styles.manaBoostMultiplier}>{isManaBoosted && '2x'}</span>   {mana}/{maxMana}</Debug>
    </div >
  );
});
